<template>
  <el-card v-loading="loading">
    <template #header>
      <div class="title1">
        <span>库存数量</span>
        <el-col :span="12">
          <el-date-picker
            v-model="datePicker"
            type="daterange"
            placeholder="请选择"
            style="width: 100%"
            @change="datePickerChange"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            :clearable="false"
          />
        </el-col>
      </div>
    </template>
    <div ref="chart" class="chart"></div>
    <el-table
      height="240px"
      :header-cell-style="{ 'text-align': 'center' }"
      :cell-style="{ 'text-align': 'center' }"
      :data="tableData"
    >
      <el-table-column
        label="排名"
        type="index"
        width="60"
        :index="indexMethod"
      />
      <el-table-column label="物料名称" prop="name" />
      <el-table-column label="库存" prop="totalQuantity" />
    </el-table>
    <el-pagination
      v-if="pages.total > 0"
      background
      style="margin-top: 10px"
      :current-page="pages.page"
      :page-size="pages.size"
      :page-sizes="[5, 10, 20, 40]"
      :total="pages.total"
      layout="->, total, prev, pager, next, sizes, jumper"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />
  </el-card>
</template>

<script setup>
import * as echarts from "echarts/core";
import {
  GridComponent,
  TooltipComponent,
  GraphicComponent,
} from "echarts/components";
import { LineChart } from "echarts/charts";
import { UniversalTransition } from "echarts/features";
import { CanvasRenderer } from "echarts/renderers";
import { queryTable } from "@/api/inventory";
import { quantityLineChart } from "@/api/statistics";

import {
  reactive,
  getCurrentInstance,
  watchEffect,
  onUnmounted,
  onMounted,
  nextTick,
  ref,
  watch,
} from "vue";
import { fmt } from "a-calc/es";

echarts.use([
  GridComponent,
  LineChart,
  CanvasRenderer,
  UniversalTransition,
  TooltipComponent,
  GraphicComponent,
]);

const props = defineProps({
  trend: {
    type: Object,
    default: () => {},
  },
});

const option = reactive({
  tooltip: {
    trigger: "axis",
  },
  legend: {
    data: ["入库数量", "出库数量"],
  },
  xAxis: {
    type: "category",
    boundaryGap: false,
    data: [],
  },
  yAxis: {
    type: "value",
  },
  series: [
    {
      name: "入库数量",
      data: [],
      type: "line",
      areaStyle: {},
      smooth: true,
    },
    {
      name: "出库数量",
      data: [],
      type: "line",
      areaStyle: {},
      smooth: true,
    },
  ],
  graphic: {
    elements: [
      {
        type: "text",
        left: "center",
        top: "middle",
        silent: true,
        invisible: false,
        style: {
          fill: "black",
          fontWeight: "bold",
          text: "暂无数据",
          fontSize: "26px",
        },
        z: 200,
      },
    ],
  },
});

const charts = {
  chart: null,
};

const loading = ref(false);

const tableData = ref([]);

const { proxy } = getCurrentInstance();

// 分页数据
const pages = reactive({
  page: 1,
  size: 5,
  total: 0,
});
const handleSizeChange = (val) => {
  pages.size = val;
  pages.page = 1;
  getList();
};
const handleCurrentChange = (val) => {
  pages.page = val;
  getList();
};
const indexMethod = (index) => {
  // 计算连续序号
  return (pages.page - 1) * pages.size + index + 1;
};
// 初始化图表

const initChart = () => {
  if (charts.chart) charts.chart.dispose();
  const chartDom = proxy.$refs.chart;
  charts.chart = echarts.init(chartDom);
  charts.chart.setOption(option);

  const resizeHandler = () => {
    charts.chart.resize();
  };

  window.addEventListener("resize", resizeHandler);

  watchEffect(() => {
    onUnmounted(() => {
      window.removeEventListener("resize", resizeHandler);
      charts.chart.dispose();
    });
  });
};

// 时间范围选择器变化
const datePickerChange = (val) => {
  searchData.startTime = val?.[0] + " 00:00:00" ?? null;
  searchData.endTime = val?.[1] + " 23:59:59" ?? null;
  getChart();
};

// 搜索表单
const searchData = reactive({
  startTime: null,
  endTime: null,
});

// 日期范围选择器
const datePicker = ref("");

// 获取列表数据
const getList = () => {
  queryTable({
    page: pages.page,
    limit: pages.size,
    descs: "total_quantity",
  }).then((res) => {
    tableData.value = res.data.records;
    pages.total = res.data.total;
  });
};

const getChart = () => {
  if (!searchData.endTime || !searchData.startTime) return;
  quantityLineChart({ ...searchData }).then((res) => {
    option.series[0].data = res.data.map((item) => item.inboundQuantity);
    option.series[1].data = res.data.map((item) => item.outboundQuantity);
    option.xAxis.data = res.data.map((item) => item.snapshotDate.split(" ")[0]); // 截取日期部分
    option.graphic.elements[0].invisible = true;
    initChart();
  });
};

// watch(
//   () => {
//     return props.trend;
//   },
//   (newVal) => {
//     if (newVal.inventoryDayDetails) {
//       const month = new Date().getMonth() + 1;
//       newVal.inventoryDayDetails.forEach((item) => {
//         option.xAxis.data.push(month + "." + item.day);
//         option.series[0].data.push(item.quantity || 0);
//       });
//       option.graphic.elements[0].invisible =
//         newVal.inventoryDayDetails.length == 0 ? false : true;
//       charts.chart.setOption(option);
//     }
//   }
// );

const getLastWeekRange = () => {
  // 获取当前日期
  let now = new Date();

  // // 获取当前日期的时间戳
  // let currentTimestamp = now.getTime();

  // 计算一个月前的时间戳
  let oneMonthAgo = new Date(now);
  oneMonthAgo.setMonth(now.getMonth() - 1);

  // 格式化日期
  function formatDate(date) {
    let year = date.getFullYear();
    let month = (date.getMonth() + 1).toString().padStart(2, "0");
    let day = date.getDate().toString().padStart(2, "0");
    // let hours = date.getHours().toString().padStart(2, "0");
    // let minutes = date.getMinutes().toString().padStart(2, "0");
    // let seconds = date.getSeconds().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  // 获取一月前和当前时间的格式化字符串
  let oneMonthAgoFormatted = formatDate(oneMonthAgo);
  let nowFormatted = formatDate(now);

  datePicker.value = [oneMonthAgoFormatted, nowFormatted];
  searchData.startTime = datePicker.value?.[0] + " 00:00:00" ?? null;
  searchData.endTime = datePicker.value?.[1] + " 23:59:59" ?? null;
};

onMounted(() => {
  getLastWeekRange();
  getList();
  getChart();
  nextTick(() => {
    initChart();
  });
});
</script>

<style scoped>
.chart {
  height: 250px;
  width: 100%;
}
.title1 {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 20px;
}
</style>
